import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { SiteNavigation } from "../components/navigation/SiteNavigation";
import { toast } from "sonner";
import {
  Sparkles, MessageCircle, Heart, Send, Trash2, Pencil, User, Flame, ArrowLeft,
  Image, Video, Bookmark, Repeat2, X
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import ReportButton from "../components/ReportButton";
import BlockUserButton from "../components/BlockUserButton";
import PlatformEmbed from "../components/PlatformEmbed";
import SponsorsCarousel from "../components/SponsorsCarousel";
import { useImageEditor } from "../components/image-editor/ImageEditorProvider";

const Community = () => {
  const editImage = useImageEditor();
  const { artistId } = useParams();
  const { isAuthenticated, token, user } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newPost, setNewPost] = useState("");
  const [posting, setPosting] = useState(false);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [expandedPost, setExpandedPost] = useState(null);
  const [replies, setReplies] = useState({});
  const [replyText, setReplyText] = useState("");
  const [replyPosting, setReplyPosting] = useState(false);
  const [artistInfo, setArtistInfo] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchPosts();
    if (artistId) {
      axios.get(`${API}/artists/${artistId}`).then(r => setArtistInfo(r.data)).catch(() => {});
    }
  }, [artistId]);

  const fetchPosts = async () => {
    try {
      const url = artistId ? `${API}/community/posts?artist_id=${artistId}` : `${API}/community/posts`;
      const res = await axios.get(url);
      setPosts(res.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const handleMediaUpload = async (e) => {
    let file = e.target.files?.[0];
    if (!file) return;
    // Accept iPhone HEIC/HEIF alongside standard images/videos
    const okType = file.type.startsWith("image/") || file.type.startsWith("video/") ||
                   /\.(heic|heif|jpg|jpeg|png|gif|webp|mp4|mov|webm)$/i.test(file.name);
    if (!okType) { toast.error("Only images and videos are allowed"); return; }
    if (file.size > 50 * 1024 * 1024) {
      toast.error(`File too large — ${(file.size / 1024 / 1024).toFixed(1)}MB. The limit is 50MB. Try compressing it first.`);
      e.target.value = "";
      return;
    }
    file = await editImage(file, { maxBytes: 50 * 1024 * 1024 });
    if (!file) { e.target.value = ""; return; }
    setUploading(true);
    const toastId = toast.loading(`Uploading ${(file.size / 1024 / 1024).toFixed(1)}MB…`);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/community/upload-media`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
      });
      setMediaPreview({ url: res.data.url, type: res.data.type });
      toast.success("Uploaded — ready to post", { id: toastId });
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || "Upload failed";
      toast.error(detail, { id: toastId });
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handlePost = async () => {
    if (!isAuthenticated) { navigate("/auth"); return; }
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const urls = newPost.match(urlRegex);
    if (!newPost.trim() && !mediaPreview && (!urls || urls.length === 0)) return;
    setPosting(true);
    try {
      const body = { text: newPost.trim(), visibility: "community" };
      if (artistId) body.artist_id = artistId;
      if (mediaPreview?.type === "image") body.image_url = mediaPreview.url;
      if (mediaPreview?.type === "video") body.video_url = mediaPreview.url;
      if (urls && urls.length > 0) body.link_url = urls[0];
      await axios.post(`${API}/community/posts`, body, { headers: { Authorization: `Bearer ${token}` } });
      setNewPost("");
      setMediaPreview(null);
      fetchPosts();
      toast.success("Posted!");
    } catch (e) { toast.error("Failed to post"); } finally { setPosting(false); }
  };

  const handleRepost = async (post) => {
    if (!isAuthenticated) { navigate("/auth"); return; }
    try {
      await axios.post(`${API}/community/posts`, {
        text: "",
        repost_id: post.post_id
      }, { headers: { Authorization: `Bearer ${token}` } });
      fetchPosts();
      toast.success("Reposted!");
    } catch (e) { toast.error("Failed to repost"); }
  };

  const handleLike = async (postId) => {
    if (!isAuthenticated) { navigate("/auth"); return; }
    try {
      await axios.post(`${API}/community/posts/${postId}/like`, {}, { headers: { Authorization: `Bearer ${token}` } });
      fetchPosts();
    } catch (e) { console.error(e); }
  };

  const handleBookmark = async (postId) => {
    if (!isAuthenticated) { navigate("/auth"); return; }
    try {
      const res = await axios.post(`${API}/community/posts/${postId}/bookmark`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.bookmarked ? "Bookmarked!" : "Removed bookmark");
    } catch (e) { console.error(e); }
  };

  const handleDelete = async (postId) => {
    try {
      await axios.delete(`${API}/community/posts/${postId}`, { headers: { Authorization: `Bearer ${token}` } });
      fetchPosts();
      toast.success("Post deleted");
    } catch (e) { toast.error("Failed to delete"); }
  };

  const [editingPostId, setEditingPostId] = useState(null);
  const [editingText, setEditingText] = useState("");

  const startEdit = (post) => {
    setEditingPostId(post.post_id);
    setEditingText(post.text || "");
  };

  const cancelEdit = () => {
    setEditingPostId(null);
    setEditingText("");
  };

  const saveEdit = async (postId) => {
    if (!editingText.trim()) { toast.error("Post cannot be empty"); return; }
    try {
      await axios.patch(
        `${API}/community/posts/${postId}`,
        { text: editingText.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Post updated");
      cancelEdit();
      fetchPosts();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update"); }
  };

  const loadReplies = async (postId) => {
    try {
      const res = await axios.get(`${API}/community/posts/${postId}/replies`);
      setReplies(prev => ({ ...prev, [postId]: res.data }));
    } catch (e) { console.error(e); }
  };

  const toggleReplies = (postId) => {
    if (expandedPost === postId) { setExpandedPost(null); return; }
    setExpandedPost(postId);
    loadReplies(postId);
  };

  const handleReply = async (postId) => {
    if (!replyText.trim()) return;
    setReplyPosting(true);
    try {
      await axios.post(`${API}/community/posts/${postId}/replies`, { text: replyText.trim() }, { headers: { Authorization: `Bearer ${token}` } });
      setReplyText("");
      loadReplies(postId);
      fetchPosts();
    } catch (e) { toast.error("Failed to reply"); } finally { setReplyPosting(false); }
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h`;
    return `${Math.floor(hrs / 24)}d`;
  };

  return (
    <div className="min-h-screen bg-[#050810]" data-testid="community-page">
      <header className="fixed top-0 left-0 right-0 z-50 glass-strong public-navigation-header">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/home" className="flex items-center gap-2" data-testid="logo-link">
            <img src="/logo.png" alt="StardomFans" className="w-10 h-10 object-contain" style={{ mixBlendMode: "lighten" }} />
          </Link>
          <SiteNavigation scope="community" />
          <div className="flex items-center gap-3">
            {isAuthenticated ? (
              <Button onClick={() => navigate("/fan-dashboard")} className="bg-[#EC008C] text-[#000000] hover:bg-[#FF3399] rounded-full px-6">My Dashboard</Button>
            ) : (
              <Button onClick={() => navigate("/auth")} className="bg-[#EC008C] text-[#000000] hover:bg-[#FF3399] rounded-full px-6">Get Started</Button>
            )}
          </div>
        </div>
      </header>

      <main className="pt-24 pb-20 px-6">
        <div className="max-w-2xl mx-auto">
          {/* Featured Sponsors — global feed only, not per-artist */}
          {!artistId && <SponsorsCarousel days={7} />}
          {/* Header */}
          <div className="text-center mb-8">
            {artistId && artistInfo ? (
              <>
                <Link to="/community" className="text-sm text-[#8A92A0] hover:text-[#EC008C] mb-4 inline-flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> All Communities</Link>
                <h1 className="font-['Unbounded'] text-3xl font-bold text-white mt-2">{artistInfo.stage_name}'s <span className="text-[#EC008C]">Community</span></h1>
              </>
            ) : (
              <>
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#D14A85]/10 border border-[#D14A85]/30 mb-4">
                  <Flame className="w-4 h-4 text-[#D14A85]" />
                  <span className="text-sm text-[#D14A85] font-medium">New Hotness</span>
                </div>
                <h1 className="font-['Unbounded'] text-3xl sm:text-4xl font-bold text-white mb-2">
                  Be <span className="text-[#EC008C] italic">"it."</span>
                </h1>
                <p className="text-[#8A92A0]">The fandom community. Post, share, connect.</p>
              </>
            )}
          </div>

          {/* Post Composer */}
          <div className="card p-5 mb-6" data-testid="post-composer">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-[#1A2030] flex items-center justify-center flex-shrink-0 overflow-hidden">
                {user?.picture ? <img src={user.picture} alt="" className="w-full h-full object-cover" /> : <User className="w-5 h-5 text-[#8A92A0]" />}
              </div>
              <div className="flex-1">
                <Textarea
                  value={newPost}
                  onChange={(e) => setNewPost(e.target.value)}
                  placeholder={isAuthenticated ? "What's on your mind?" : "Sign in to post..."}
                  className="post-composer-textarea bg-[#050810] border-[#1A2030] text-white resize-y min-h-[160px] max-h-[500px] overflow-y-auto text-base leading-relaxed p-4"
                  disabled={!isAuthenticated}
                  data-testid="post-textarea"
                />
                {/* Media Preview */}
                {mediaPreview && (
                  <div className="relative mt-3 rounded-xl overflow-hidden">
                    {mediaPreview.type === "image" ? (
                      <img src={mediaPreview.url} alt="Edited post preview" className="block mx-auto w-auto h-auto max-w-full max-h-[480px] rounded-xl" />
                    ) : (
                      <video src={mediaPreview.url} className="w-full max-h-64 rounded-xl" controls />
                    )}
                    <button onClick={() => setMediaPreview(null)} className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/70 flex items-center justify-center text-white hover:bg-black">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
                <div className="flex items-center justify-between mt-3">
                  <div className="flex items-center gap-2">
                    <input data-testid="community-media-input" ref={fileInputRef} type="file" accept="image/*,video/*,.heic,.heif" onChange={handleMediaUpload} className="hidden" />
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={!isAuthenticated || uploading}
                      className="p-2 rounded-lg text-[#8A92A0] hover:text-[#EC008C] hover:bg-[#EC008C]/10 transition-colors disabled:opacity-50"
                      title="Upload image"
                      data-testid="upload-image-btn"
                    >
                      <Image className="w-5 h-5" />
                    </button>
                    <span className="text-xs text-[#4A5260]">{newPost.length}/1000</span>
                    {uploading && <span className="text-xs text-[#EC008C]">Uploading...</span>}
                  </div>
                  <Button
                    onClick={handlePost}
                    disabled={posting || (!newPost.trim() && !mediaPreview)}
                    className="bg-[#EC008C] text-[#000000] hover:bg-[#FF3399] rounded-full px-6"
                    data-testid="post-submit-btn"
                  >
                    {posting ? "Posting..." : <><Send className="w-4 h-4 mr-1" /> Post</>}
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Posts */}
          {loading ? (
            <div className="flex justify-center py-20"><div className="spinner" /></div>
          ) : posts.length === 0 ? (
            <div className="card p-12 text-center">
              <MessageCircle className="w-12 h-12 text-[#8A92A0] mx-auto mb-4" />
              <h3 className="text-white font-semibold text-lg mb-2">No posts yet</h3>
              <p className="text-[#8A92A0] text-sm">Be the first to start the conversation!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {posts.map((post) => (
                <div key={post.post_id} className="card p-5" data-testid={`post-${post.post_id}`}>
                  {/* Repost indicator */}
                  {post.repost_id && (
                    <div className="flex items-center gap-2 text-xs text-[#8A92A0] mb-3 pb-2 border-b border-[#1A2030]">
                      <Repeat2 className="w-3.5 h-3.5" />
                      <span>{post.user_name} reposted</span>
                    </div>
                  )}
                  
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#1A2030] flex-shrink-0 overflow-hidden flex items-center justify-center">
                      {post.user_picture ? <img src={post.user_picture} alt="" className="w-full h-full object-cover" /> : <User className="w-5 h-5 text-[#8A92A0]" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-white text-sm">{post.user_name}</span>
                        <span className="text-xs text-[#4A5260]">{timeAgo(post.created_at)}</span>
                      </div>
                      
                      {/* Post text */}
                      {editingPostId === post.post_id ? (
                        <div className="mt-2 space-y-2" data-testid={`edit-form-${post.post_id}`}>
                          <textarea
                            value={editingText}
                            onChange={(e) => setEditingText(e.target.value)}
                            rows={3}
                            className="w-full p-3 bg-[#050810] border border-[#EC008C]/30 rounded-lg text-white text-sm focus:outline-none focus:border-[#EC008C]"
                            data-testid={`edit-textarea-${post.post_id}`}
                          />
                          <div className="flex gap-2">
                            <button
                              onClick={() => saveEdit(post.post_id)}
                              className="px-3 py-1.5 rounded-full bg-[#EC008C] text-black text-xs font-bold hover:bg-[#FF3399]"
                              data-testid={`edit-save-${post.post_id}`}
                            >Save</button>
                            <button
                              onClick={cancelEdit}
                              className="px-3 py-1.5 rounded-full border border-[#1A2030] text-[#8A92A0] text-xs hover:text-white"
                              data-testid={`edit-cancel-${post.post_id}`}
                            >Cancel</button>
                          </div>
                        </div>
                      ) : (
                        post.text && <p className="text-[#8A92A0] mt-2 text-sm whitespace-pre-wrap break-words">{post.text}</p>
                      )}
                      
                      {/* Repost content */}
                      {post.repost_data && (
                        <div className="mt-3 p-3 rounded-xl border border-[#1A2030] bg-[#050810]">
                          <p className="text-xs text-[#4A5260] mb-1">@{post.repost_data.user_name}</p>
                          {post.repost_data.text && (
                            <p className="text-sm text-[#8A92A0] whitespace-pre-wrap break-words">{post.repost_data.text}</p>
                          )}
                          {post.repost_data.image_url && <img src={post.repost_data.image_url} alt="" className="mt-2 block mx-auto w-auto h-auto max-w-full max-h-[480px] rounded-lg" />}
                          {post.repost_data.video_url && (
                            <video src={post.repost_data.video_url} className="mt-2 rounded-lg w-full max-h-80" controls preload="metadata" />
                          )}
                          {post.repost_data.link_url && <PlatformEmbed url={post.repost_data.link_url} meta={post.repost_data.link_meta} />}
                        </div>
                      )}
                      
                      {/* Media */}
                      {post.image_url && (
                        <div className="mt-3 rounded-xl overflow-hidden">
                          <img src={post.image_url} alt="" className="block mx-auto w-auto h-auto max-w-full max-h-[640px] rounded-xl" />
                        </div>
                      )}
                      {post.video_url && (
                        <div className="mt-3 rounded-xl overflow-hidden">
                          <video src={post.video_url} className="w-full max-h-80 rounded-xl" controls preload="metadata" />
                        </div>
                      )}
                      {post.link_url && <PlatformEmbed url={post.link_url} meta={post.link_meta} />}
                      
                      {/* Actions */}
                      <div className="flex items-center gap-5 mt-3">
                        <button onClick={() => handleLike(post.post_id)} className="flex items-center gap-1 text-[#8A92A0] hover:text-[#D14A85] transition-colors text-sm" data-testid={`like-${post.post_id}`}>
                          <Heart className="w-4 h-4" /> {post.likes || 0}
                        </button>
                        <button onClick={() => toggleReplies(post.post_id)} className="flex items-center gap-1 text-[#8A92A0] hover:text-[#FF6BC5] transition-colors text-sm" data-testid={`comments-${post.post_id}`}>
                          <MessageCircle className="w-4 h-4" /> {post.comment_count || 0}
                        </button>
                        <button onClick={() => handleRepost(post)} className="flex items-center gap-1 text-[#8A92A0] hover:text-[#8DB87A] transition-colors text-sm" data-testid={`repost-${post.post_id}`}>
                          <Repeat2 className="w-4 h-4" /> {post.repost_count || 0}
                        </button>
                        <button onClick={() => handleBookmark(post.post_id)} className="flex items-center gap-1 text-[#8A92A0] hover:text-[#EC008C] transition-colors text-sm ml-auto" data-testid={`bookmark-${post.post_id}`}>
                          <Bookmark className="w-4 h-4" /> {post.bookmark_count || 0}
                        </button>
                        {user && post.user_id === user.user_id && (
                          <>
                            <button onClick={() => startEdit(post)} className="text-[#8A92A0] hover:text-[#EC008C] transition-colors" data-testid={`edit-${post.post_id}`} title="Edit post">
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button onClick={() => handleDelete(post.post_id)} className="text-[#8A92A0] hover:text-[#D14A85] transition-colors" data-testid={`delete-${post.post_id}`}>
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        {user && post.user_id !== user.user_id && (
                          <>
                            <ReportButton contentType="post" contentId={post.post_id} token={token} iconOnly />
                            <BlockUserButton targetUserId={post.user_id} targetName={post.user_name} token={token} onBlocked={fetchPosts} iconOnly />
                          </>
                        )}
                      </div>

                      {/* Replies */}
                      {expandedPost === post.post_id && (
                        <div className="mt-4 pt-4 border-t border-[#1A2030] space-y-3" data-testid={`replies-${post.post_id}`}>
                          {(replies[post.post_id] || []).map((r) => (
                            <div key={r.reply_id} className="flex items-start gap-2 pl-2">
                              <div className="w-7 h-7 rounded-full bg-[#1A2030] flex-shrink-0 overflow-hidden flex items-center justify-center">
                                {r.user_picture ? <img src={r.user_picture} alt="" className="w-full h-full object-cover" /> : <User className="w-3.5 h-3.5 text-[#8A92A0]" />}
                              </div>
                              <div>
                                <span className="text-xs font-medium text-white">{r.user_name}</span>
                                <span className="text-xs text-[#4A5260] ml-2">{timeAgo(r.created_at)}</span>
                                <p className="text-xs text-[#8A92A0] mt-0.5">{r.text}</p>
                              </div>
                            </div>
                          ))}
                          {isAuthenticated && (
                            <div className="flex items-center gap-2 pl-2">
                              <input
                                value={replyText}
                                onChange={(e) => setReplyText(e.target.value)}
                                placeholder="Write a reply..."
                                className="flex-1 bg-[#050810] border border-[#1A2030] rounded-lg px-3 py-2 text-xs text-white placeholder-[#52525B]"
                                data-testid={`reply-input-${post.post_id}`}
                              />
                              <button
                                onClick={() => handleReply(post.post_id)}
                                disabled={replyPosting || !replyText.trim()}
                                className="text-[#EC008C] hover:text-[#FF3399] disabled:opacity-50"
                                data-testid={`reply-submit-${post.post_id}`}
                              >
                                <Send className="w-4 h-4" />
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Community;
